import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Sparkles, Trash2, Copy, Check, ShieldCheck, Minus, Bot, User } from 'lucide-react';
import '../styles/vedic-ai-chat-modal.css';

const QUICK_SUGGESTIONS = [
  { label: 'Assess Prakriti 🌿', query: 'I want to assess my Prakriti profile and dosha balance.' },
  { label: 'Shalya Principles ⚔️', query: 'What are the core surgical principles (Shalya Tantra) in Sushruta Samhita?' },
  { label: 'AYUSH Treatments 🩺', query: 'What are AYUSH recommended treatments for joint inflammation?' },
  { label: 'Agnikarma & Jalauka 🩸', query: 'Explain the indications for Agnikarma and Leech Therapy (Jalaukavacharana).' },
];

const DEFAULT_WELCOME_MESSAGE = {
  id: 'welcome-1',
  sender: 'assistant',
  text: 'Namaste 🙏! I am **Vedic AI Assistant**, your specialized guide for Shalya Tantra surgical wisdom, AYUSH clinical recommendations, and Ayurvedic medical education.\n\nHow can I help you today?',
  timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
};

const VedicAiChatModal = ({ isOpen, onClose, onMinimize, initialQuery = null }) => {
  const [messages, setMessages] = useState([DEFAULT_WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change or typing status changes
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isTyping, isOpen]);

  // If opened with an initial query from welcome popup
  useEffect(() => {
    if (isOpen && initialQuery) {
      handleSendMessage(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialQuery]);

  const handleSendMessage = async (textToSend) => {
    const text = textToSend || inputValue.trim();
    if (!text) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMessage]);
    if (!textToSend) setInputValue('');
    setIsTyping(true);

    // If Botpress script is active on the window, dispatch to Botpress session as well
    if (window.botpress && typeof window.botpress.sendMessage === 'function') {
      try {
        window.botpress.sendMessage({ type: 'text', payload: { text } });
      } catch (err) {
        console.warn('Botpress sync note:', err);
      }
    }

    // Simulate / fetch AI Response
    setTimeout(() => {
      let aiResponseText = generateVedicResponse(text);

      const aiMessage = {
        id: `assistant-${Date.now()}`,
        sender: 'assistant',
        text: aiResponseText,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      setMessages((prev) => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1200);
  };

  const generateVedicResponse = (query) => {
    const q = query.toLowerCase();
    if (q.includes('prakriti')) {
      return "**Prakriti Assessment (Ayurvedic Bio-Constitution)** 🌿\n\nPrakriti represents your unique genetic & anatomical constitution determined by Vata, Pitta, and Kapha doshas:\n\n1. **Vata Dominance**: Lean build, fast movement, dry skin, prone to joint pain.\n2. **Pitta Dominance**: Moderate build, sharp intellect, warm body temperature, prone to inflammation.\n3. **Kapha Dominance**: Solid build, calm demeanor, soft skin, slow digestion.\n\n👉 *You can complete our full clinical Prakriti Questionnaire under the AYUSH Clinical Portal!*";
    }
    if (q.includes('shalya') || q.includes('sushruta') || q.includes('surgical')) {
      return "**Classical Shalya Tantra Principles (Sushruta Samhita)** ⚔️\n\nMaharshi Sushruta classified surgical interventions into **Ashtavidha Shastra Karma** (8 Primary Surgical Procedures):\n\n• **Chedana** (Excision/Amputation)\n• **Bhedana** (Incision/Opening)\n• **Lekhana** (Scraping/Debridement)\n• **Vedhana** (Puncturing/Venesection)\n• **Eshana** (Probing)\n• **Aharya** (Extraction of foreign bodies)\n• **Visravana** (Fluid Drainage)\n• **Seevana** (Suturing)\n\n*All procedures emphasize strict Pre-operative (Purva Karma), Operative (Pradhana Karma), and Post-operative (Paschat Karma) protocols.*";
    }
    if (q.includes('agnikarma') || q.includes('jalauka') || q.includes('leech')) {
      return "**Para-Surgical Procedures (Anushastra Karma)** 🩸\n\n1. **Agnikarma (Thermal Cautery)**: Superior for chronic musculoskeletal pain, heel spurs (Vatakataka), and Arshas (piles). Prevents recurrence.\n2. **Jalaukavacharana (Leech Therapy)**: Indicated for Pitta-Rakta disorders, venous stasis ulcers, eczema, and localized ischemia. Hirudin in leech saliva acts as a potent anticoagulant & anti-inflammatory agent.";
    }
    if (q.includes('ayush') || q.includes('treatment') || q.includes('joint')) {
      return "**AYUSH Standard Clinical Guidelines** 🩺\n\nFor chronic inflammatory and musculoskeletal conditions:\n\n• **Herbal Formulations**: Shallaki (Boswellia serrata), Guggulu preparations (Yogaraja/Kaishora Guggulu), Rasna Saptaka Kwatha.\n• **External Therapies**: Abhyanga with Mahanarayana Taila, Patra Pinda Sweda (herbal poultice fomentation).\n• **Pathya (Dietary Advice)**: Warm cooked meals, ghee, turmeric, ginger, avoiding cold/incompatible foods (Viruddhahara).";
    }
    return `Thank you for your query regarding **"${query}"**.\n\nOur platform connects classical Shalya Tantra text annotations from Sushruta Samhita with modern clinical protocols.\n\nFor detailed research papers, textbook chapters, and procedure guides, explore our **Articles & Research Catalog** under the Study menu!`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setMessages([DEFAULT_WELCOME_MESSAGE]);
  };

  const handleCopyMessage = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 30 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { type: 'spring', damping: 25, stiffness: 220 }
    },
    exit: { opacity: 0, scale: 0.9, y: 20, transition: { duration: 0.2 } }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="vedic-chat-overlay">
        <motion.div
          className="vedic-chat-drawer"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Header */}
          <div className="vedic-chat-header">
            <div className="vedic-chat-brand">
              <div className="vedic-chat-avatar-container">
                <div className="chat-avatar-ring" />
                <img src="/bot-avatar.svg" alt="Vedic AI Logo" className="vedic-chat-avatar" />
                <span className="chat-status-dot" />
              </div>
              <div className="vedic-chat-title-info">
                <div className="vedic-chat-title">
                  <span>Vedic AI Assistant</span>
                  <Sparkles size={14} className="sparkle-cyan" />
                </div>
                <div className="vedic-chat-subtitle">
                  <ShieldCheck size={11} className="icon-shield" />
                  <span>Vedic Surgery • AYUSH AI Engine</span>
                </div>
              </div>
            </div>

            <div className="vedic-chat-controls">
              <button
                className="chat-control-btn"
                onClick={handleClearChat}
                title="Clear Chat History"
              >
                <Trash2 size={15} />
              </button>
              {onMinimize && (
                <button
                  className="chat-control-btn"
                  onClick={onMinimize}
                  title="Minimize"
                >
                  <Minus size={15} />
                </button>
              )}
              <button
                className="chat-control-btn close-btn"
                onClick={onClose}
                title="Close Chat"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages Feed */}
          <div className="vedic-chat-messages">
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                className={`vedic-chat-bubble-row ${msg.sender === 'user' ? 'row-user' : 'row-assistant'}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="bubble-avatar-mark">
                  {msg.sender === 'user' ? (
                    <User size={14} className="icon-user-svg" />
                  ) : (
                    <Bot size={14} className="icon-bot-svg" />
                  )}
                </div>

                <div className="bubble-content-wrapper">
                  <div className="bubble-meta">
                    <span className="bubble-sender-name">
                      {msg.sender === 'user' ? 'You' : 'Vedic Assistant'}
                    </span>
                    <span className="bubble-timestamp">{msg.timestamp}</span>
                  </div>

                  <div className="bubble-text">
                    {msg.text.split('\n').map((line, idx) => (
                      <React.Fragment key={idx}>
                        {renderFormattedText(line)}
                        {idx < msg.text.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </div>

                  {msg.sender === 'assistant' && (
                    <button
                      className="bubble-copy-btn"
                      onClick={() => handleCopyMessage(msg.id, msg.text)}
                      title="Copy response"
                    >
                      {copiedId === msg.id ? (
                        <>
                          <Check size={12} className="text-green" />
                          <span>Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy size={12} />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Live Typing Indicator */}
            {isTyping && (
              <div className="vedic-chat-bubble-row row-assistant">
                <div className="bubble-avatar-mark">
                  <Bot size={14} className="icon-bot-svg" />
                </div>
                <div className="bubble-content-wrapper">
                  <div className="bubble-text typing-indicator-bubble">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Prompt Chips */}
          <div className="vedic-chat-quick-chips">
            {QUICK_SUGGESTIONS.map((item, idx) => (
              <button
                key={idx}
                className="chat-quick-chip"
                onClick={() => handleSendMessage(item.query)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Footer Input Area */}
          <div className="vedic-chat-footer">
            <div className="vedic-chat-input-wrapper">
              <textarea
                className="vedic-chat-textarea"
                rows={1}
                placeholder="Ask about Shalya Tantra, surgery, or AYUSH protocols..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button
                className="vedic-chat-send-btn"
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim()}
                title="Send Message"
              >
                <Send size={15} />
              </button>
            </div>
            <div className="vedic-chat-disclaimer">
              <span>Powered by Vedic AI • Educational & Surgical Reference</span>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Helper function to handle basic markdown formatting (*bold* & lists)
const renderFormattedText = (line) => {
  const parts = line.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

export default VedicAiChatModal;
